<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋客戶代碼、客戶名稱或平台名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group" v-if="isSystemAccount">
        <label>平台篩選：</label>
        <select
          v-model="selectedPlatformId"
          class="form-control platform-filter"
          @change="handlePlatformFilter"
        >
          <option value="">全部平台</option>
          <option
            v-for="platform in platforms"
            :key="platform.id"
            :value="platform.id"
          >
            {{ platform.name }} ({{ platform.code }})
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchClients">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>平台</th>
            <th>客戶名稱 / 代碼</th>
            <th>方案名稱</th>
            <th>訂閱狀態</th>
            <th>到期日</th>
            <th>成功扣款</th>
            <th>最後成功扣款</th>
            <th>最後失敗扣款</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="clients.length === 0">
            <td colspan="9" class="empty-state">
              沒有找到客戶
            </td>
          </tr>
          <tr v-for="client in clients" :key="client.id">
            <td>{{ client.platform_name || '-' }}</td>
            <td>
              <span class="client-name">{{ client.client_name }}</span> / {{ client.client_code }}
            </td>
            <td>
              <span v-if="client.plan_name" :class="getPlanBadgeClass(client.plan_code)">
                {{ client.plan_name }}
              </span>
              <span v-else class="text-muted">未指定</span>
            </td>
            <td>
              <span :class="getSubscriptionStatusClass(client.subscription_status)">
                {{ getSubscriptionStatusText(client.subscription_status) }}
              </span>
            </td>
            <td>{{ formatDate(client.subscription_end_date) }}</td>
            <td>
              <span v-if="client.successful_payment_count > 0">
                {{ client.successful_payment_count }} 次<br>
                <small>NT$ {{ formatCurrency(client.successful_total_amount) }}</small>
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>{{ formatDate(client.last_successful_payment_date) }}</td>
            <td>
              <span v-if="client.last_failed_payment_date" class="text-danger">
                {{ formatDate(client.last_failed_payment_date) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-primary"
                  @click="handleViewDetails(client)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />

    <!-- 詳細資訊彈窗 -->
    <DetailModal
      v-if="showDetailModal"
      :client-id="selectedClientId"
      @close="showDetailModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { subscriptionClientsService } from '@/api/modules/subscription-clients.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import { showError } from '@/utils/swal.js';
import authService from '@/helpers/authservice/authService.js';
import DetailModal from './Detail.vue';

export default {
  name: 'SubscriptionClientsList',
  components: {
    DetailModal
  },
  setup() {
    const { pageTitle } = usePageTitle('訂閱客戶管理');
    const clients = ref([]);
    const platforms = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const selectedPlatformId = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);
    const showDetailModal = ref(false);
    const selectedClientId = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const fetchPlatforms = async () => {
      // 只有系統帳號需要獲取平台列表
      if (!isSystemAccount.value) {
        return;
      }
      try {
        const response = await platformManagementService.getPlatforms({ 
          page: 1, 
          pageSize: 100,
          status: 'active'
        });
        
        if (response && response.success && response.data) {
          if (response.data.items) {
            platforms.value = response.data.items.map(p => ({
              id: p.id,
              name: p.platformName,
              code: p.platformCode
            }));
          }
        }
      } catch (err) {
        console.error('獲取平台列表失敗:', err);
      }
    };

    const fetchClients = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        // 只有系統帳號可以指定平台篩選
        if (isSystemAccount.value && selectedPlatformId.value && selectedPlatformId.value !== '') {
          params.platformId = selectedPlatformId.value;
        }

        const response = await subscriptionClientsService.getSubscriptionClients(params);

        if (response && response.success && response.data) {
          clients.value = response.data.items || [];
          total.value = response.data.total || 0;
        } else {
          error.value = response && response.message || '獲取訂閱客戶列表失敗';
        }
      } catch (err) {
        console.error('獲取訂閱客戶列表失敗:', err);
        error.value = err.message || '獲取訂閱客戶列表失敗';
        await showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchClients();
      }, 500);
    };

    const handlePlatformFilter = () => {
      currentPage.value = 1;
      fetchClients();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchClients();
      }
    };

    const handleViewDetails = (client) => {
      selectedClientId.value = client.id;
      showDetailModal.value = true;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('zh-TW', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    };

    const getPlanBadgeClass = (planCode) => {
      if (!planCode) return 'badge badge-secondary';
      
      const planCodeLower = planCode.toLowerCase();
      if (planCodeLower.includes('basic') || planCodeLower.includes('標準') || planCodeLower.includes('基礎')) {
        return 'badge badge-info';
      } else if (planCodeLower.includes('standard') || planCodeLower.includes('標準')) {
        return 'badge badge-primary';
      } else if (planCodeLower.includes('premium') || planCodeLower.includes('進階') || planCodeLower.includes('高級')) {
        return 'badge badge-purple';
      } else if (planCodeLower.includes('enterprise') || planCodeLower.includes('企業')) {
        return 'badge badge-orange';
      } else if (planCodeLower.includes('pro') || planCodeLower.includes('專業')) {
        return 'badge badge-warning';
      } else {
        const colors = ['badge-info', 'badge-primary', 'badge-purple', 'badge-orange', 'badge-warning'];
        let hash = 0;
        for (let i = 0; i < planCode.length; i++) {
          hash = planCode.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colorIndex = Math.abs(hash) % colors.length;
        return `badge ${colors[colorIndex]}`;
      }
    };

    const getSubscriptionStatusClass = (status) => {
      const statusMap = {
        'trial': 'badge badge-info',
        'active': 'badge badge-success',
        'grace_period': 'badge badge-warning',
        'expired': 'badge badge-danger',
        'no_subscription': 'badge badge-secondary'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getSubscriptionStatusText = (status) => {
      const statusMap = {
        'trial': '試用中',
        'active': '啟用中',
        'grace_period': '寬限期',
        'expired': '已過期',
        'no_subscription': '無訂閱'
      };
      return statusMap[status] || status || '未知';
    };

    onMounted(() => {
      fetchPlatforms();
      fetchClients();
    });

    return {
      clients,
      platforms,
      loading,
      error,
      searchQuery,
      selectedPlatformId,
      currentPage,
      totalPages,
      total,
      showDetailModal,
      selectedClientId,
      isSystemAccount,
      pageTitle,
      handleSearch,
      handlePlatformFilter,
      goToPage,
      handleViewDetails,
      formatDate,
      formatCurrency,
      getPlanBadgeClass,
      getSubscriptionStatusClass,
      getSubscriptionStatusText,
      fetchClients,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  white-space: nowrap;
  font-weight: 500;
}

.platform-filter {
  min-width: 200px;
}

.table-wrapper table {
  min-width: 1600px;
}

.text-danger {
  color: #dc3545;
}

.text-muted {
  color: #999;
  font-style: italic;
}

.client-name {
  color: #2563eb;
  font-weight: 500;
}

/* 響應式設計 - 手機版 */
@media (max-width: 768px) {
  .page-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-box {
    flex: 1;
    max-width: 100%;
    width: 100%;
  }

  .filter-group {
    width: 100%;
  }

  .platform-filter {
    width: 100%;
  }
}
</style>
